import {
  Divider,
  FormControlLabel,
  GKButton,
  GKTextField,
  Grid2,
  Switch,
  Typography,
} from "components";
import usePermission from "hooks/usePermission";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import DashboardLayout from "layouts/DashboardLayout";
import _ from "lodash";
import React from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { ROLE_PERMISSION_BY_ID } from "utils/apiRoutes";
import axiosInstance from "utils/axiosInstance";

interface Permission {
  permission: string;
  subHeading: string;
  isExist?: string[];
}

interface PermissionGroup {
  mainHeading: string;
  subPermission: Permission[];
}

interface FormData {
  name: string;
  permissions: {
    [key: string]: boolean;
  };
}

const CreateRoleWisePermission: React.FC = () => {
  const { id, role } = useParams();
  const navigate = useNavigate();

  const { permissions, permissionLoading } = usePermission({
    role: role?.toUpperCase(),
  });

  const { data, isLoading: roleByIdLoading } = useFetchQuery({
    key: ["ROLE_BY_ID", id],
    route: ROLE_PERMISSION_BY_ID(id),
    enabled: !!id,
  });

  const { mutate: createRole, isLoading } = useMutation({
    mutationFn: (data: FormData) =>
      axiosInstance.post(`/role_permissions/`, data),
    onSuccess: () => {
      navigate(-1);
    },
  });

  const { mutate: updateRole, isLoading: updateRoleLoading } = useMutation({
    mutationFn: (data: FormData) =>
      axiosInstance.patch(`/role_permissions/${id}/`, data),
    onSuccess: () => {
      navigate(-1);
    },
  });

  const defaultValues: FormData = {
    name: "",
    permissions: _.reduce(
      permissions,
      (acc: { [key: string]: boolean }, permissionGroup: PermissionGroup) => {
        _.forEach(permissionGroup.subPermission, (permission) => {
          acc[permission.permission] = false;
        });
        return acc;
      },
      {},
    ),
  };

  const { control, setValue, watch, handleSubmit } = useForm<FormData>({
    defaultValues,
    values: data?.data,
  });

  const handleChangePermission = (permission: string, value: boolean) => {
    setValue(`permissions.${permission}` as const, value);
  };

  const onSubmit: SubmitHandler<FormData> = (data: FormData) => {
    if (id) {
      updateRole(data);
    } else {
      createRole(data);
    }
  };

  const handleToggleAllPermissions = (
    e: React.ChangeEvent<HTMLInputElement>,
    permissionGroup: PermissionGroup,
  ) => {
    _.forEach(permissionGroup.subPermission, (permission) => {
      setValue(`permissions.${permission.permission}`, e.target.checked);
    });
  };

  return (
    <DashboardLayout
      title="Create Role"
      loading={(!!id && roleByIdLoading) || permissionLoading}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid2 container spacing={3}>
          <Grid2 size={12}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <GKTextField
                  {...field}
                  inputLabel={id ? "Edit Role Name" : "Enter Role Name"}
                />
              )}
            />
          </Grid2>

          {_.map(permissions, (dataItem, index) => {
            const allPermissionsEnabled = _.every(
              dataItem.subPermission,
              (permission) =>
                watch(`permissions.${permission.permission}` as const),
            );
            return (
              <Grid2 container size={12} spacing={2}>
                <Grid2
                  size={3}
                  display={"flex"}
                  alignItems={"flex-start"}
                  key={index}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={allPermissionsEnabled}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleToggleAllPermissions(e, dataItem);
                        }}
                      />
                    }
                    label={
                      <Typography fontSize={16} fontWeight={600}>
                        {dataItem.mainHeading}
                      </Typography>
                    }
                  />
                </Grid2>
                <Grid2
                  container
                  size={9}
                  spacing={2}
                  display={"inline-flex"}
                  alignItems={"center"}
                >
                  {_.map(dataItem.subPermission, (data, subIndex) => (
                    <Grid2
                      size={6}
                      display={"flex"}
                      alignItems={"center"}
                      key={subIndex}
                    >
                      <Controller
                        name={`permissions.${data.permission}` as const}
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            {...field}
                            control={
                              <Switch
                                sx={{ marginTop: "1px" }}
                                checked={field.value}
                                onChange={(e) =>
                                  handleChangePermission(
                                    data.permission,
                                    e.target.checked,
                                  )
                                }
                              />
                            }
                            label={
                              <Typography
                                fontSize={14}
                                fontWeight={500}
                                sx={{
                                  textOverflow: "ellipsis",
                                  overflow: "hidden",
                                  // whiteSpace: "nowrap",
                                  width: "100%",
                                }}
                              >
                                {data.heading}
                              </Typography>
                            }
                            sx={{
                              alignItems: "center",
                              flexWrap: "nowrap",
                              width: "100%",
                            }}
                          />
                        )}
                      />
                    </Grid2>
                  ))}
                </Grid2>
                <Grid2 size={12}>
                  <Divider />
                </Grid2>
              </Grid2>
            );
          })}
        </Grid2>
        <Grid2
          container
          display={"flex"}
          justifyContent={"flex-end"}
          alignItems={"center"}
          mt={2}
        >
          <GKButton
            type="submit"
            variant="contained"
            loading={isLoading || updateRoleLoading}
            size="medium"
          >
            {id ? "Update" : "Save"}
          </GKButton>
        </Grid2>
      </form>
    </DashboardLayout>
  );
};

export default CreateRoleWisePermission;
